'use client';

/**
 * AskSurface — the rail-resident Ask Klyde peer surface.
 *
 * Fifth peer alongside Insights / Copilot / Composer / Chat. Hosts a
 * persistent agent thread reachable from every mode via the EdgeStrip
 * Ask icon.
 *
 * Backend: `docs_ask` (the same strategy Reception uses) — chosen for
 * v1 to get reliable, source-grounded answers for the "how does Klyde
 * X" questions the universal rail needs to handle well. Mode8 stays on
 * `ki_ask`. The swap to ki_ask (once it grows a docs-intent branch
 * delegating here internally) happens later.
 *
 * v1 scope:
 *  - Chat input + multi-turn thread (conversation_id threaded back).
 *  - Renders short_answer inline as bubbles.
 *  - Follow-up suggestions rendered as quick-tap chips.
 *  - Long answer + sources + grounded docs open in the canvas overlay
 *    when there's a renderer_card hint OR when long_answer is
 *    meaningfully larger than short_answer.
 *
 * @component_role: Card Surface
 * @component_type: Layout Peer
 * @domain: Frontend Shell / Agent
 * @layer: presentation
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Send, Sparkle, RotateCcw, Maximize2 } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useAskRailStore, type AskRailTurn } from '@/lib/stores/ask-rail-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import type { DocsResponse, DocsRequest } from '@/lib/api/types';
import { cn } from '@/lib/utils/cn';

/**
 * Wire envelope for docs_ask — the gateway returns
 * `{success, data, renderer_card?, error, strategy}`. docs_ask's
 * renderer_card sits on `DocsResponse.renderer_card` (set when the
 * agent decides a card-rendered answer is appropriate, e.g. "show me
 * the Monte Carlo for 2026"). Hoist it onto the response so the
 * canvas-overlay auto-open logic can read it without re-fetching.
 */
async function askDocs(params: DocsRequest): Promise<DocsResponse> {
  const res = await api.getStrategyData<DocsResponse>('docs_ask', params);
  if (!res.success) {
    throw new Error(res.error || 'docs_ask failed');
  }
  return res.data;
}

export function AskSurface() {
  // Affordance address — derived test_id from the matrix wire. The
  // @affordance(key="input", surface="rail.ask") decoration is the
  // source; this hook reads the projected value.
  const inputTestId = useAffordance('rail.ask.input');
  const turns = useAskRailStore((s) => s.turns);
  const isLoading = useAskRailStore((s) => s.isLoading);
  const isLlmLoading = useAskRailStore((s) => s.isLlmLoading);
  const conversationId = useAskRailStore((s) => s.conversationId);
  const setResponse = useAskRailStore((s) => s.setResponse);
  const upgradeLatestTurn = useAskRailStore((s) => s.upgradeLatestTurn);
  const setLoading = useAskRailStore((s) => s.setLoading);
  const setLlmLoading = useAskRailStore((s) => s.setLlmLoading);
  const setLastQuery = useAskRailStore((s) => s.setLastQuery);
  const clear = useAskRailStore((s) => s.clear);

  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Captures the query at submit time so onSuccess can build a turn
  // with the right text. Ref avoids re-binding the mutation on every
  // keystroke; `.current` reads the latest value when onSuccess fires.
  const lastQueryRef = useRef<string | null>(null);

  // Phase 2 — the deeper-synthesis tier (mode='full'). Fired only when
  // the fast tier returned llm_status='auto_running'. Upgrades the same
  // turn in place (no second bubble). The overlay is user-controlled
  // (Open in canvas affordance on the bubble); the rail never auto-
  // opens it on the user's behalf.
  const askDeep = useMutation<DocsResponse, Error, DocsRequest>({
    mutationFn: askDocs,
    onSuccess: (response) => {
      upgradeLatestTurn(response);
    },
    onError: () => setLlmLoading(false),
  });

  // Phase 1 — the fast tier (mode='auto'). Returns the cached SP
  // summary in ~1.8s without an LLM round-trip. If llm_status comes
  // back as 'auto_running', kick off the deeper-synthesis mutation
  // in the background; the rail input stays usable while it runs.
  const askFast = useMutation<DocsResponse, Error, DocsRequest>({
    mutationFn: askDocs,
    onSuccess: (response) => {
      setResponse(response);
      setQuery('');
      if (response.llm_status === 'auto_running') {
        setLlmLoading(true);
        askDeep.mutate({
          query: lastQueryRef.current ?? '',
          conversation_id: response.conversation_id ?? conversationId,
          mode: 'full',
        });
      }
    },
    onError: () => setLoading(false),
  });

  // Auto-scroll the thread to the latest turn (or to the in-flight
  // user message during loading) so the user sees what just happened.
  // Also re-scrolls when the LLM tier upgrades — the bubble grew.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns.length, isLoading, isLlmLoading]);

  /** Submit a question.
   *
   * `parentConversationId` controls threading:
   *  - `null` (typed input) → fresh `:Conversation`. The retrieval
   *    prompt isn't biased by prior turns; clean topic isolation. The
   *    use case: a new top-level question that has nothing to do with
   *    what was just asked.
   *  - `<sid>` (chip click) → threaded to that bubble's parent
   *    conversation. DocsOrchestrator's CONDENSE phase prepends the
   *    prior question; the LLM sees the running transcript via
   *    `record_turn()`. The use case: "tell me more about that",
   *    "give me an example", "what about for X?".
   *
   * Each top-level question starts a fresh conversation by default.
   * Follow-up chips on a specific turn always thread to *that turn's*
   * conversation (not the latest in the store) — so clicking a chip
   * on an older bubble drills into the right context even if newer
   * top-level questions opened other conversations after it.
   */
  const submitQuery = (text: string, parentConversationId: string | null = null) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    lastQueryRef.current = trimmed;
    setLastQuery(trimmed);
    setLoading(true);
    askFast.mutate({
      query: trimmed,
      conversation_id: parentConversationId,
      // mode='auto' — fast cached SP tier (~1.8s, no LLM). If the
      // backend signals llm_status='auto_running' we fire the deeper
      // mode='full' tier behind it (askDeep above).
      mode: 'auto',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Typed input = new search. No parent conversation; backend opens
    // a fresh CV. The visual rail thread still appends a bubble; only
    // the backend retrieval context is reset.
    submitQuery(query, null);
  };

  return (
    <div className="flex flex-col h-full bg-background border-l border-border">
      <SurfaceHeader hasThread={turns.length > 0} onReset={clear} />

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {turns.length === 0 && !isLoading && <EmptyState />}
        {turns.map((turn, i) => (
          <TurnPair
            key={i}
            turn={turn}
            // Follow-ups thread to *this turn's* conversation, not the
            // store's latest. So clicking a chip on an older bubble
            // drills into the right context even if a newer top-level
            // question opened another conversation after it.
            onFollowup={(q) => submitQuery(q, turn.response.conversation_id ?? null)}
            // Only the latest turn gets the deepening indicator —
            // the LLM tier upgrades that bubble in place.
            isDeepening={i === turns.length - 1 && isLlmLoading}
          />
        ))}
        {isLoading && <ThinkingBubble />}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-border p-2">
        <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5 focus-within:border-primary/50">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask Klyde anything…"
            disabled={isLoading}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50"
            aria-label="Ask Klyde"
            data-testid={inputTestId}
          />
          <button
            type="submit"
            disabled={!query.trim() || isLoading}
            className={cn(
              'rounded p-1 text-muted-foreground transition-colors',
              query.trim() && !isLoading && 'text-primary hover:bg-primary/10',
              (!query.trim() || isLoading) && 'opacity-40',
            )}
            aria-label="Send"
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function SurfaceHeader({
  hasThread,
  onReset,
}: {
  hasThread: boolean;
  onReset: () => void;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-3 py-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Sparkle className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-tight">Ask Klyde</div>
        <div className="text-[10px] text-muted-foreground leading-tight">
          docs-grounded · in the rail
        </div>
      </div>
      {hasThread && (
        <button
          onClick={onReset}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title="Start a new conversation"
          aria-label="Start a new conversation"
        >
          <RotateCcw className="h-3 w-3" />
          New
        </button>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground py-8">
      <Sparkle className="h-6 w-6 text-primary/60 mb-2" />
      <div className="text-xs max-w-[220px] leading-relaxed">
        Ask anything about Klyde — what it does, how it works, what it costs.
      </div>
    </div>
  );
}

function TurnPair({
  turn,
  onFollowup,
  isDeepening,
}: {
  turn: AskRailTurn;
  onFollowup: (query: string) => void;
  isDeepening: boolean;
}) {
  const openOverlay = useAgentCanvasStore((s) => s.openWith);
  const openInCanvasTestId = useAffordance('rail.ask.open_in_canvas');
  // Affordance shows whenever the BFF shipped a renderer_card hint —
  // pure render of the BFF's signal, no frontend heuristic. The user
  // decides whether to expand into the canvas overlay.
  const hasOverlay = !!turn.response.renderer_card;

  return (
    <div className="space-y-2">
      <UserBubble text={turn.query} />
      <AgentBubble response={turn.response} isDeepening={isDeepening} />
      {/* Follow-up suggestions (when present) — quick-tap chips that
          submit the suggestion as the next turn. */}
      {turn.response.followup_suggestions && turn.response.followup_suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1 pl-7">
          {turn.response.followup_suggestions.slice(0, 4).map((s, i) => (
            <button
              key={i}
              onClick={() => onFollowup(s)}
              className={cn(
                'rounded-full border border-border bg-muted/30 px-2 py-0.5 text-[10px]',
                'text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {/* Re-open affordance — when this turn has a renderer_card or a
          meaningful long_answer worth seeing in the bigger surface. */}
      {hasOverlay && (
        <div className="flex justify-start pl-7">
          <button
            onClick={() => openOverlay(turn)}
            className={cn(
              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]',
              'text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
            )}
            aria-label="Open in canvas"
            title="Open in canvas overlay"
            data-testid={openInCanvasTestId}
          >
            <Maximize2 className="h-3 w-3" />
            Open in canvas
          </button>
        </div>
      )}
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-lg rounded-tr-sm bg-primary/15 px-2.5 py-1.5 text-xs">
        {text}
      </div>
    </div>
  );
}

function AgentBubble({
  response,
  isDeepening,
}: {
  response: DocsResponse;
  isDeepening: boolean;
}) {
  // Polymorphic on response_type. docs_ask returns answer / clarification
  // / no_docs / error.
  if (response.response_type === 'error') {
    return (
      <AgentBlock>
        <div className="text-xs text-destructive">
          {response.short_answer || 'Something went wrong.'}
        </div>
      </AgentBlock>
    );
  }
  if (response.response_type === 'no_docs') {
    return (
      <AgentBlock>
        <div className="text-xs text-muted-foreground">
          {response.short_answer || "I couldn't find anything in the docs that matches."}
        </div>
      </AgentBlock>
    );
  }
  // answer / clarification — render the fast tier's short answer as
  // the primary bubble text (stable across the two-tier round-trip),
  // and stack the LLM tier's deeper synthesis as an additional block
  // when it lands. `response.short_answer` is a convenience accessor
  // that mirrors whichever tier is current — using `summary.fast.short`
  // explicitly keeps the original cached-SP answer visible after the
  // LLM upgrade overwrites the top-level field.
  const status = response.llm_status;
  const fastText =
    response.summary?.fast?.short ??
    response.short_answer ??
    response.long_answer ??
    '';
  const llmText = response.summary?.llm?.short ?? null;
  // Only show the deeper block when the LLM tier exists AND said
  // something different from the fast tier — no point repeating the
  // same sentence under a different label.
  const showDeeper = !!llmText && llmText.trim() !== fastText.trim();
  return (
    <AgentBlock>
      <div className="text-xs leading-relaxed whitespace-pre-wrap">{fastText}</div>
      {showDeeper && (
        <div className="mt-2 border-l-2 border-primary/40 pl-2">
          <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-primary">
            <Sparkle className="h-2.5 w-2.5" />
            Deeper synthesis
          </div>
          <div className="text-xs leading-relaxed whitespace-pre-wrap">{llmText}</div>
        </div>
      )}
      {(response.sources?.length ?? 0) > 0 && (
        <div className="mt-1.5 text-[10px] text-muted-foreground">
          {response.sources.length} source{response.sources.length === 1 ? '' : 's'}
          {response.confidence && response.confidence !== 'none' && (
            <span className="ml-1.5">· confidence: {response.confidence}</span>
          )}
          {status === 'completed' && (
            <span className="ml-1.5 text-primary">· deeper synthesis</span>
          )}
        </div>
      )}
      {isDeepening && (
        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-primary">
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
          Synthesising a deeper answer…
        </div>
      )}
    </AgentBlock>
  );
}

function AgentBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-start gap-1.5">
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Sparkle className="h-3 w-3" />
      </div>
      <div className="max-w-[90%] rounded-lg rounded-tl-sm border border-border bg-card px-2.5 py-1.5">
        {children}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start gap-1.5">
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
        <Sparkle className="h-3 w-3" />
      </div>
      <div className="rounded-lg rounded-tl-sm border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          Thinking…
        </span>
      </div>
    </div>
  );
}
